import { Fragment } from 'react';
import { EnvironmentMarker } from '@gonfalon/environments/EnvironmentMarker';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { getProjectSdkKeysQuery } from '@gonfalon/rest-api/queries/sdkKeys';
import { ProgressBar } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQueries } from '@tanstack/react-query';

import { DiffBeforeAfter } from '../DiffBeforeAfter';
import { PayloadChangeDialogBase } from '../PayloadChangeDialogBase';
import { type ChangeableResourceType, groupSdkKeysByEnvironment, type SdkKeyEnvGroup } from '../utils';

import styles from '../../../PayloadFilters/diffTable.module.css';
import ownStyles from './ConfirmViewResourceChangeDialog.module.css';

export type { ChangeableResourceType };

export type ConfirmViewResourceChangeDialogProps = {
  /** Controls modal visibility. Parent owns the state so the underlying UI stays mounted underneath. */
  isOpen: boolean;
  /** Called when the user dismisses via Cancel, the close button, or the overlay. */
  onCancel: () => void;
  /** Fires the actual mutation. The shell stays mounted on rejection so the user can retry. */
  onConfirm: () => Promise<void>;
  /** Whether flags or segments are changing. Drives the unit copy in the diff rows. */
  resourceType: ChangeableResourceType;
  /**
   * Count of resources being added in this action. Default 0. Combine with
   * `removedCount` to express mixed save-the-edits flows where some resources
   * are linked and others unlinked in the same mutation.
   */
  addedCount: number;
  /** Count of resources being removed in this action. Default 0. See `addedCount`. */
  removedCount: number;
  /**
   * Views whose payload-active SDK keys are about to be updated. Always an array —
   * single-view callers pass a 1-element array; multi-view callers (create-with-attach,
   * flag-detail add-to-views) pass N. The dialog runs one query per view and aggregates
   * the affected SDK keys into a single env-grouped table. Names are required so the
   * alert can state exactly which views drive the list (REL-15078) — with multi-view
   * callers the table alone can't attribute keys to views, and a generic "this view"
   * reads as if only the view the user just picked is affected.
   */
  views: Array<{ key: string; name: string }>;
  /** Project containing the views. */
  projectKey: string;
  /** External submitting flag — typically wired from the parent mutation's `isPending`. */
  isSubmitting: boolean;
  /**
   * Optional override for the primary action button label. Forwarded to
   * `PayloadChangeDialogBase`. Defaults to "Update"; the flag-creation flow
   * passes `"Continue"` because the dialog precedes the actual
   * `POST /flags` rather than confirming an in-progress edit.
   */
  confirmLabel?: string;
  isCountUnknown?: boolean;
};

function formatCount(count: number, resourceType: ChangeableResourceType): string {
  return resourceType === 'flags'
    ? msg('sdkKeys.viewResourceChangeDialog.flagCount', { count })
    : msg('sdkKeys.viewResourceChangeDialog.segmentCount', { count });
}

/**
 * Render the bolded view names inline in the alert, joined by commas — the
 * same treatment `ConfirmMobileSdkPayloadDialog` uses.
 */
function renderViewNames(views: Array<{ key: string; name: string }>) {
  return views.map((entry, idx) => (
    <Fragment key={entry.key}>
      {idx > 0 ? ', ' : null}
      <span className={ownStyles.viewName}>{entry.name}</span>
    </Fragment>
  ));
}

/**
 * View-centric alert copy — addresses the bot finding that the default shell
 * alert ("All SDKs using this key…") reads as if the user is editing an SDK
 * key. Here the user is editing a view's resource list, so the verb + preposition
 * track the actual operation: add (to) / remove (from) / both = update (on).
 *
 * The affected views are named explicitly (REL-15078). The table below unions
 * SDK keys across every targeted view, so without the names a multi-view
 * confirmation (e.g. create-flag with several views attached, including ones
 * pre-populated from the flag list's view context) looks like it lists keys
 * unrelated to the view the user just picked.
 */
function AlertBody({
  resourceType,
  addedCount,
  removedCount,
  views,
  isCountUnknown,
}: {
  resourceType: ChangeableResourceType;
  addedCount: number;
  removedCount: number;
  views: Array<{ key: string; name: string }>;
  isCountUnknown: boolean;
}) {
  const noun =
    resourceType === 'flags'
      ? msg('sdkKeys.viewResourceChangeDialog.flagsNoun')
      : msg('sdkKeys.viewResourceChangeDialog.segmentsNoun');
  const alertValues = {
    noun,
    viewCount: views.length,
    views: () => renderViewNames(views),
  };
  if (isCountUnknown || (addedCount > 0 && removedCount === 0)) {
    return (
      <p>
        <Message id="sdkKeys.viewResourceChangeDialog.addAlert" values={alertValues} />
      </p>
    );
  }
  if (removedCount > 0 && addedCount === 0) {
    return (
      <p>
        <Message id="sdkKeys.viewResourceChangeDialog.removeAlert" values={alertValues} />
      </p>
    );
  }
  return (
    <p>
      <Message id="sdkKeys.viewResourceChangeDialog.updateAlert" values={alertValues} />
    </p>
  );
}

/**
 * Confirmation dialog for adding flags/segments to — or removing them from —
 * payload-active view(s). Wraps the shared [[confirm-payload-change-dialog]] shell
 * with an env-grouped table of affected SDK keys plus a `before → after` diff per
 * row. The alert names every targeted view so the union table below can't be
 * misread as scoped to a single view (REL-15078).
 *
 * The dialog owns its data: it queries `getProjectSdkKeysQuery` once per view,
 * unions the results, dedupes by SDK key id, and groups by environment.
 *
 * Diff math per row: `afterCount = max(before + addedCount − removedCount, 0)`.
 *
 * When the list endpoint doesn't expose `viewSummaries`, falls back to an
 * upper-bound approximation — "+N {unit}" / "-N {unit}", using whichever
 * of `addedCount` / `removedCount` is larger to pick the dominant direction.
 */
export function ConfirmViewResourceChangeDialog({
  isOpen,
  onCancel,
  onConfirm,
  resourceType,
  addedCount,
  removedCount,
  views,
  projectKey,
  isSubmitting,
  confirmLabel,
  isCountUnknown = false,
}: ConfirmViewResourceChangeDialogProps) {
  // One query per view. `useQueries` keeps the contract uniform whether
  // `views` has 1 or N entries. `combine` aggregates results inside the query
  // layer so the consumer never needs a variable-length useMemo dep array
  // (which would violate React's stable-dep-array contract).
  const { isLoading, isError, envGroups } = useQueries({
    queries: views.map((view) => ({
      ...getProjectSdkKeysQuery({
        projectKey,
        query: {
          filter: { viewKey: view.key },
          expand: ['environmentSummary'],
        },
        header: { 'LD-API-Version': 'beta' },
      }),
      enabled: isOpen,
    })),
    // Aggregate SDK keys across all view queries, deduping by SDK key id so a
    // key bound to multiple targeted views only appears once. Building a Map
    // from [key, item] pairs does the dedupe inline — the duplicate entry from
    // the second query is the same SDK key entity, so last-wins is harmless.
    combine: (results) => {
      const items = results.flatMap((q) => q.data?.items ?? []);
      const merged = Array.from(new Map(items.map((item) => [item.key, item])).values());
      return {
        isLoading: results.some((q) => q.isLoading),
        isError: results.some((q) => q.isError),
        envGroups: groupSdkKeysByEnvironment(merged, resourceType),
      };
    },
  });

  return (
    <PayloadChangeDialogBase
      isOpen={isOpen}
      onCancel={onCancel}
      onConfirm={onConfirm}
      isSubmitting={isSubmitting}
      sectionHeading={msg('sdkKeys.viewResourceChangeDialog.affectedPayloads')}
      alert={
        <AlertBody
          resourceType={resourceType}
          addedCount={addedCount}
          removedCount={removedCount}
          views={views}
          isCountUnknown={isCountUnknown}
        />
      }
      confirmLabel={confirmLabel}
    >
      <DialogBody
        isLoading={isLoading}
        isError={isError}
        envGroups={envGroups}
        addedCount={addedCount}
        removedCount={removedCount}
        resourceType={resourceType}
        isCountUnknown={isCountUnknown}
      />
    </PayloadChangeDialogBase>
  );
}

function DialogBody({
  isLoading,
  isError,
  envGroups,
  addedCount,
  removedCount,
  resourceType,
  isCountUnknown,
}: {
  isLoading: boolean;
  isError: boolean;
  envGroups: SdkKeyEnvGroup[];
  addedCount: number;
  removedCount: number;
  resourceType: ChangeableResourceType;
  isCountUnknown: boolean;
}) {
  if (isLoading) {
    return (
      <div className={styles.stateBlock}>
        <ProgressBar aria-label={msg('sdkKeys.viewResourceChangeDialog.loadingLabel')} isIndeterminate />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.stateBlock} role="alert">
        <p className={styles.stateMessage}>
          <Message id="sdkKeys.viewResourceChangeDialog.loadError" />
        </p>
      </div>
    );
  }

  if (envGroups.length === 0) {
    return (
      <div className={styles.stateBlock}>
        <p className={styles.stateMessage}>
          <Message id="sdkKeys.viewResourceChangeDialog.emptyState" />
        </p>
      </div>
    );
  }

  return (
    <div
      className={styles.envGroupsList}
      role="list"
      aria-label={msg('sdkKeys.viewResourceChangeDialog.affectedPayloads')}
    >
      {envGroups.map((group) => (
        <section key={group.envKey} className={styles.envGroup} role="listitem">
          <header className={styles.envHeader}>
            <EnvironmentMarker color={group.envColor} className={styles.envMarker} />
            <span className={styles.envName} title={group.envName}>
              {group.envName}
            </span>
          </header>
          <ul
            className={styles.keyList}
            aria-label={msg('sdkKeys.viewResourceChangeDialog.keyListLabel', { envName: group.envName })}
          >
            {group.keys.map((sdkKey) => (
              <li key={sdkKey.key} className={styles.keyRow}>
                <span className={styles.keyLabel}>
                  <Icon name="key" size="small" />
                  <span className={styles.keyName} title={sdkKey.name}>
                    {sdkKey.name}
                  </span>
                </span>
                {isCountUnknown ? null : (
                  <KeyDiff
                    beforeCount={sdkKey.beforeCount}
                    addedCount={addedCount}
                    removedCount={removedCount}
                    resourceType={resourceType}
                  />
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function KeyDiff({
  beforeCount,
  addedCount,
  removedCount,
  resourceType,
}: {
  beforeCount: number | undefined;
  addedCount: number;
  removedCount: number;
  resourceType: ChangeableResourceType;
}) {
  // Approximation when the list endpoint doesn't yet expose `viewSummaries`.
  // Picks the dominant axis (the larger of added
  // vs removed) so mixed actions still surface a single, honest upper bound.
  if (beforeCount === undefined) {
    const dominantCount = Math.max(addedCount, removedCount);
    const direction = addedCount >= removedCount ? 'add' : 'remove';
    return (
      <span className={styles.approximate}>
        {resourceType === 'flags' ? (
          <Message id="sdkKeys.viewResourceChangeDialog.approxFlagDelta" values={{ direction, count: dominantCount }} />
        ) : (
          <Message
            id="sdkKeys.viewResourceChangeDialog.approxSegmentDelta"
            values={{ direction, count: dominantCount }}
          />
        )}
      </span>
    );
  }

  const afterCount = Math.max(beforeCount + addedCount - removedCount, 0);
  return (
    <DiffBeforeAfter before={formatCount(beforeCount, resourceType)} after={formatCount(afterCount, resourceType)} />
  );
}
